import { Phone, MapPin, Star, Droplets, Flame, Bath } from 'lucide-react';
import Seo from '../components/Seo';
import Reveal from '../components/Reveal';
import ArrowLink from '../components/ArrowLink';
import PackageCard from '../components/PackageCard';
import FaqAccordion from '../components/FaqAccordion';
import { IMAGES, PACKAGES } from '../data/content';

const NEEDS = [
  {
    icon: Phone,
    title: 'Emergency jobs ring you, not your competitor',
    text: 'When a pipe bursts, nobody fills in a long form — they tap the first phone number they see. Your site puts a click-to-call button on every page, thumb-height on a mobile.',
  },
  {
    icon: MapPin,
    title: 'You show up for “plumber near me”',
    text: 'A complete Google Business Profile with the right categories, service areas and photos is what puts a local plumber on the map. We set it up properly and guide you through Google’s verification.',
  },
  {
    icon: Star,
    title: 'Your reputation is visible in seconds',
    text: 'Customers check reviews before they let anyone near their boiler. We make your best reviews and finished work impossible to miss.',
  },
];

const WORK_TYPES = [
  {
    icon: Droplets,
    title: 'Emergency repairs',
    text: 'Leaks, bursts and blockages — the jobs where being first and looking credible wins the call.',
  },
  {
    icon: Flame,
    title: 'Boiler & heating work',
    text: 'Higher-value jobs where customers compare carefully. A proper services page and real photos do the selling.',
  },
  {
    icon: Bath,
    title: 'Bathroom installations',
    text: 'Big-ticket projects booked weeks ahead. A before-and-after gallery turns browsers into enquiries.',
  },
];

const PLUMBER_FAQS = [
  {
    q: 'I get most of my work from word of mouth. Why do I need a website?',
    a: 'Because recommendations now get checked online. When someone is passed your name, the first thing they do is search for you. A professional website and Google profile means that search strengthens the recommendation instead of ending it — and catches the customers who never got a recommendation at all.',
  },
  {
    q: 'Can you set me up to appear for emergency call-outs?',
    a: 'Your Google Business Profile and website will make your contact details, hours and service areas clear — exactly what emergency customers look for. What nobody can honestly promise is a specific position in Google’s results; we set everything up to give you the best genuine chance.',
  },
  {
    q: 'I cover several towns. Can the site reflect that?',
    a: 'Yes. Your site lists the areas you cover in plain language, and your Google profile is set up as a service-area business, so you’re not tied to a single street address you don’t trade from.',
  },
  {
    q: 'I’m not good with technology. How much work is this for me?',
    a: 'About an hour in total, spread over the build: one questionnaire, some photos of your work from your phone, and a quick look at the finished site before launch. Everything else is done for you.',
  },
];

export default function PlumbersPage() {
  return (
    <>
      <Seo
        title="Websites for Plumbers — Get Found Locally | Build My Trade"
        description="Professional websites, Google Business Profile setup and social pages built specifically for UK plumbers. Fixed packages from £249, plain English, everything handled."
        path="/websites-for-plumbers"
        image={IMAGES.plumbingHero.url}
      />

      {/* Hero */}
      <section className="bg-charcoal bg-blueprint" data-testid="plumbers-hero">
        <div className="mx-auto grid max-w-7xl items-center gap-14 px-4 pb-24 pt-32 sm:px-6 sm:pt-40 lg:grid-cols-2 lg:px-8">
          <div>
            <p className="font-mono text-xs font-semibold uppercase tracking-[0.2em] text-lime">
              Websites for plumbers
            </p>
            <h1 className="mt-5 text-balance font-heading text-4xl font-extrabold leading-[1.08] tracking-tight text-offwhite sm:text-5xl lg:text-6xl">
              A website that books jobs <span className="text-lime">while you’re under a sink.</span>
            </h1>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-offwhite/70 sm:text-lg">
              Build My Trade sets up everything a plumber needs to be found and trusted locally: a fast,
              mobile-friendly website, a complete Google Business Profile and branded social pages — for one fixed,
              published price.
            </p>
            <div className="mt-9 flex flex-wrap gap-4">
              <ArrowLink to="/pricing" variant="lime" testid="plumbers-cta-packages" className="px-7 py-3.5">
                See Fixed-Price Packages
              </ArrowLink>
              <ArrowLink to="/contact" variant="outline-light" testid="plumbers-cta-quote" className="px-7 py-3.5">
                Get a Free Quote
              </ArrowLink>
            </div>
            <p className="mt-7 text-sm text-offwhite/50">
              Straightforward packages. Clear prices. No marketing jargon.
            </p>
          </div>
          <Reveal>
            <img
              src={IMAGES.plumbingHero.url}
              alt={IMAGES.plumbingHero.alt}
              loading="eager"
              className="aspect-[4/3] w-full rounded-2xl border border-charcoal-line object-cover"
            />
          </Reveal>
        </div>
      </section>

      {/* What a plumber's online presence needs */}
      <section className="bg-offwhite" data-testid="plumbers-needs">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28">
          <Reveal className="max-w-3xl">
            <p className="font-mono text-xs font-semibold uppercase tracking-[0.2em] text-stonebrand">
              Built around how plumbing work actually comes in
            </p>
            <h2 className="mt-4 text-balance font-heading text-2xl font-bold leading-snug tracking-tight text-charcoal sm:text-3xl lg:text-4xl">
              What a plumber’s website has to do
            </h2>
          </Reveal>
          <div className="mt-14 grid gap-5 lg:grid-cols-3">
            {NEEDS.map((n, i) => (
              <Reveal key={i} delay={0.08 * i} className="h-full">
                <div className="h-full rounded-2xl border border-hairline bg-white p-8">
                  <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-charcoal">
                    <n.icon className="h-5 w-5 text-lime" aria-hidden="true" />
                  </span>
                  <h3 className="mt-6 font-heading text-lg font-bold tracking-tight text-charcoal">{n.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-stonebrand">{n.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Work types */}
      <section className="border-y border-hairline bg-white" data-testid="plumbers-work-types">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28">
          <div className="grid items-start gap-12 lg:grid-cols-12">
            <div className="lg:col-span-5">
              <Reveal>
                <p className="font-mono text-xs font-semibold uppercase tracking-[0.2em] text-stonebrand">
                  Every kind of job you take
                </p>
                <h2 className="mt-4 text-balance font-heading text-2xl font-bold leading-snug tracking-tight text-charcoal sm:text-3xl lg:text-4xl">
                  Set up to win the work you want more of
                </h2>
                <p className="mt-5 text-base leading-relaxed text-stonebrand">
                  A good plumbing website isn’t one generic page. It speaks to each kind of customer differently —
                  the panicked emergency call, the careful boiler comparison, the bathroom project being planned for
                  months.
                </p>
              </Reveal>
              <Reveal delay={0.15} className="mt-8">
                <img
                  src={IMAGES.plumbingDetail.url}
                  alt={IMAGES.plumbingDetail.alt}
                  loading="lazy"
                  className="aspect-[4/3] w-full rounded-2xl object-cover"
                />
              </Reveal>
            </div>
            <div className="lg:col-span-7">
              <ul className="space-y-5">
                {WORK_TYPES.map((w, i) => (
                  <Reveal key={i} delay={0.08 * i}>
                    <li className="flex gap-5 rounded-2xl border border-hairline bg-offwhite-light p-7">
                      <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-lime">
                        <w.icon className="h-5 w-5 text-charcoal" aria-hidden="true" />
                      </span>
                      <div>
                        <h3 className="font-heading text-lg font-bold tracking-tight text-charcoal">{w.title}</h3>
                        <p className="mt-1.5 text-sm leading-relaxed text-stonebrand">{w.text}</p>
                      </div>
                    </li>
                  </Reveal>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Packages */}
      <section className="bg-charcoal" data-testid="plumbers-packages">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28">
          <Reveal className="max-w-3xl">
            <p className="font-mono text-xs font-semibold uppercase tracking-[0.2em] text-lime">Fixed, published prices</p>
            <h2 className="mt-4 text-balance font-heading text-2xl font-bold leading-snug tracking-tight text-offwhite sm:text-3xl lg:text-4xl">
                  Packages that fit a plumbing business
            </h2>
            <p className="mt-5 text-base leading-relaxed text-offwhite/65">
              The same straightforward packages we offer every trade — one-off payments, everything listed, nothing
              hidden. Most plumbers start with Look Professional: a proper website plus the full Google and social
              setup.
            </p>
          </Reveal>
          <div className="mt-16 grid items-stretch gap-6 lg:grid-cols-3">
            {PACKAGES.map((pkg, i) => (
              <PackageCard key={pkg.slug} pkg={pkg} delay={0.08 * i} />
            ))}
          </div>
        </div>
      </section>

      {/* Plumber FAQs */}
      <section className="bg-offwhite" data-testid="plumbers-faqs">
        <div className="mx-auto max-w-5xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28">
          <Reveal className="max-w-3xl">
            <p className="font-mono text-xs font-semibold uppercase tracking-[0.2em] text-stonebrand">
              Questions plumbers ask us
            </p>
            <h2 className="mt-4 text-balance font-heading text-2xl font-bold leading-snug tracking-tight text-charcoal sm:text-3xl lg:text-4xl">
              Straight answers for busy plumbers
            </h2>
          </Reveal>
          <Reveal delay={0.1} className="mt-10">
            <FaqAccordion items={PLUMBER_FAQS} testid="plumbers-faq" />
          </Reveal>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-charcoal-deep bg-blueprint" data-testid="plumbers-cta">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28">
          <div className="mx-auto max-w-2xl text-center">
            <Reveal>
              <h2 className="text-balance font-heading text-3xl font-extrabold tracking-tight text-offwhite sm:text-4xl">
                Ready to be the plumber people <span className="text-lime">find first?</span>
              </h2>
              <p className="mt-5 text-base leading-relaxed text-offwhite/65">
                Tell us about your plumbing business and the areas you cover — we’ll recommend the right starting
                point, in plain English, with no obligation.
              </p>
              <div className="mt-9">
                <ArrowLink to="/contact" variant="lime" testid="plumbers-cta-final" className="px-8 py-4">
                  Get Your Free Quote
                </ArrowLink>
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </>
  );
}
